// =============================================================================
// legal-admin-client.ts — Client API CGU ADMIN (visibilité cross-fournisseur), DOUBLABLE.
// =============================================================================
// Miroir des endpoints ADMIN de S5bis (apps/api/src/legal/legal-admin.controller.ts —
// CguAdminReadService, lecture cross-tenant sous withAdmin). Comme les autres clients UI
// (legal, supplier, parameters), expose une INTERFACE (LegalAdminClient) consommée par
// les hooks TanStack ; l'impl HTTP réelle (fetch) viendra derrière la MÊME interface
// sans toucher l'UI (concern transverse différé, stub-first).
//
// SÉPARATION STRICTE (décision GATE_0 S5bis) : ce client est l'accès ADMIN au statut de
// signature. Il NE remplace PAS le LegalClient fournisseur (legal-client.ts, session-
// fournisseur). La COLONNE « Signature CGU » de A-03/A-04 et la vue NON-SIGNATAIRES §2.9
// sont COMPOSÉES DANS LE FRONT : la map/le set de signataires viennent d'ICI, la liste des
// fournisseurs vient du SupplierClient (supplier-client.ts) — JAMAIS un import cross-BC ni
// une requête cross-schéma. L'anti-join non-signataires est fait côté UI.
//
// DTO = miroirs HTTP (PAS le Published Language, PAS @jeko/db).

import { HttpApiError } from '@/api/http-error';

/** Statut de signature d'un SIRET vis-à-vis de la version COURANTE (colonne A-03/A-04). */
export interface AdminSignedStatusDto {
  /** A signé la version PUBLIÉE COURANTE (false si seule une version antérieure → T-06). */
  readonly hasSigned: boolean;
  /** Version effectivement signée la plus récente (possiblement antérieure → T-06). */
  readonly signedVersionId?: string;
  /**
   * S29-CGU-NONSIGN (§2.9) — horodatage ISO 8601 de la signature la plus récente de ce SIRET.
   * Absent si jamais signé. Affiché comme « date dernière signature » dans la vue non-signataires.
   */
  readonly signedAt?: string;
}

/**
 * Map siret → statut de signature (miroir de la réponse /admin/legal/cgu/signed-status).
 * Un SIRET ABSENT = jamais signataire (le front affiche « — », jamais une valeur inventée).
 */
export type AdminSignedStatusBySiretDto = Readonly<Record<string, AdminSignedStatusDto>>;

/** Entrée du journal admin des signatures (§2.9). Sous-ensemble probatoire SANS IP en clair. */
export interface AdminSignatureJournalEntryDto {
  readonly signerSiret: string;
  readonly cguVersionId: string;
  readonly signerContactId: string;
  /** Horodatage ISO 8601 de la signature. */
  readonly acceptedAt: string;
  /**
   * IP de signature MASQUÉE CÔTÉ SERVEUR (dernier octet/hextet occulté, R-2). `null` si absente.
   * L'octet en clair (`signerIp`) NE quitte JAMAIS l'API : le front n'y a pas accès et ne l'affiche
   * jamais. Ce champ est déjà masqué à la source (cgu-admin-read.service.ts, maskSignerIp).
   */
  readonly signerIpMasked: string | null;
}

/**
 * Entrée du JOURNAL des re-signatures FORCÉES d'un fournisseur (R15-GOV-04, DBU §2.9 l.2136-2144).
 * Miroir de /admin/legal/cgu/resignature/:siret (GET). Champ de CONFORMITÉ ADMIN-RELISIBLE : le
 * `motif` est journalisé EN CLAIR et RESTITUÉ tel quel à l'admin (surface admin-only, EXCLU de Pulse) ;
 * ce n'est PAS une PII à masquer côté admin.
 *
 * DEC-47 — `actorLabel` = nom de l'administrateur RÉSOLU CÔTÉ SERVEUR (« Prénom Nom », ou « — » si non
 * résolu). L'UUID `actor_id` (subject OIDC) reste la clé d'imputabilité PERSISTÉE côté serveur mais NE
 * FRANCHIT PLUS la frontière front (minimisation R-5.1) : le front n'affiche que le libellé nominatif.
 */
export interface ForcedResignatureDto {
  /** Identifiant opaque de l'entrée de journal (key React). */
  readonly logId: string;
  /** Nom de l'administrateur ayant forcé la re-signature, RÉSOLU serveur (« Prénom Nom » ou « — »). */
  readonly actorLabel: string;
  /** SIRET du fournisseur ciblé. */
  readonly targetSiret: string;
  /** Motif de conformité, journalisé et restitué EN CLAIR (admin-relisible, DBU §2.9). */
  readonly motif: string;
  /** Horodatage ISO 8601 du forçage. */
  readonly forcedAt: string;
}

/** Journal §2.9 + version de référence (miroir de /admin/legal/cgu/signatures). */
export interface AdminSignaturesJournalDto {
  /** Version publiée courante (null si aucune). */
  readonly currentVersionId: string | null;
  /** Toutes les signatures cross-fournisseur, triées par `acceptedAt` décroissant. */
  readonly entries: readonly AdminSignatureJournalEntryDto[];
}

/** Set des signataires de la version courante (miroir de /admin/legal/cgu/signed-sirets). */
export interface AdminSignedSiretsDto {
  /** Version publiée courante (null si aucune ⇒ `sirets` vide). */
  readonly currentVersionId: string | null;
  /** SIRET distincts ayant signé `currentVersionId` (anti-join non-signataires côté front). */
  readonly sirets: readonly string[];
}

/**
 * Signataires de la version courante au grain CONTACT (miroir de /admin/legal/cgu/signed-contacts).
 * FIX-FOU-2 / DEC-46/DEC-48 — la signature CGU passe du grain SIRET au grain CONTACT : un SIRET est
 * « signataire » SSI TOUS ses contacts ACTIFS ont signé la version courante (sémantique ALL). Le BC
 * legal NE connaît PAS l'état Actif des contacts (invariant n°2) : il expose ici les contactIds AYANT
 * SIGNÉ par SIRET ; le calcul ALL (intersection avec les contacts Actifs) est composé DANS LE FRONT.
 */
export interface AdminSignedContactsDto {
  /** Version publiée courante (null si aucune ⇒ `bySiret` vide). */
  readonly currentVersionId: string | null;
  /**
   * Map siret → contactIds DISTINCTS ayant signé `currentVersionId` sous ce SIRET. Un SIRET ABSENT =
   * aucun de ses contacts n'a signé la version courante. Parité conventionnelle avec `bySiret[siret]`.
   */
  readonly bySiret: Readonly<Record<string, readonly string[]>>;
}

/** État d'une version CGU au registre (miroir CguVersionState). Libellé UI dérivé, jamais couleur seule. */
export type CguVersionStateDto = 'Brouillon' | 'Publiee' | 'Archivee';

/**
 * Ligne du REGISTRE admin des versions CGU (A-CGU ÉTAPE 2, miroir CguRegistreVersionRow).
 * 7 colonnes de cycle de vie gelées + `nbSignatures` + `hasPdf`. `createdBy`/`publishedBy` =
 * subject OIDC admin OPAQUE (résolution nom = composition front, jamais serveur — invariant n°2).
 */
export interface CguRegistreVersionRowDto {
  readonly cguVersionId: string;
  readonly versionNumber: string;
  readonly state: CguVersionStateDto;
  /** Date d'entrée en vigueur cible (ISO), ou null. */
  readonly effectiveDate: string | null;
  /** Horodatage de publication (ISO), ou null tant que non publiée. */
  readonly publishedAt: string | null;
  /** Admin publiant (subject OIDC OPAQUE). */
  readonly publishedBy: string | null;
  /** Horodatage de création du brouillon (ISO). */
  readonly createdAt: string;
  /** Admin créateur (subject OIDC OPAQUE). */
  readonly createdBy: string | null;
  /** Horodatage d'archivage (ISO), ou null. */
  readonly archivedAt: string | null;
  /** Dernière modification (ISO), ou null. */
  readonly lastModifiedAt: string | null;
  /** Nombre de signatures collectées pour cette version. */
  readonly nbSignatures: number;
  /** Vrai si la version porte un PDF horodaté téléchargeable (Publiée/Archivée). */
  readonly hasPdf: boolean;
}

/** Détail d'une version (registre + contenu intégral HTML + ancrage PDF). Miroir CguRegistreVersionDetail. */
export interface CguRegistreVersionDetailDto extends CguRegistreVersionRowDto {
  /** Corps HTML éditorial intégral (mode lecture, DBU l.2010). READ-ONLY (aucun write back). */
  readonly contentHtml: string | null;
  /** SHA-256 du PDF horodaté (ancrage probatoire), ou null si pas de PDF. */
  readonly pdfSha256: string | null;
}

/**
 * Entrée de création d'un BROUILLON (miroir du DraftBody de LegalController, back `ca0aa8b`).
 * Le corps `contentHtml` est OPTIONNEL et PERSISTÉ par l'API (elle NETTOIE le HTML côté serveur —
 * allowlist stricte ; le front n'a pas à sanitiser mais doit gérer un 400 « contenu aberrant »).
 */
export interface CreateCguDraftInput {
  /** Numéro fonctionnel `CGU-YYYY-MM` (VO, unicité côté API). */
  readonly versionNumber: string;
  /** Contenu du changelog (obligatoire pour tracer la version). */
  readonly changelog: string;
  /** Date d'entrée en vigueur cible (ISO), optionnelle. */
  readonly effectiveDate?: string;
  /** Corps HTML éditorial initial (optionnel). NETTOYÉ côté serveur — jamais sanitisé côté front. */
  readonly contentHtml?: string;
}

/**
 * Entrée d'édition d'un BROUILLON (miroir de l'EditDraftBody du LegalAdminController, back `ca0aa8b`).
 * Patch PARTIEL : `contentHtml` seul, `effectiveDate` seule, ou les deux. Un champ absent =
 * inchangé côté serveur. Le HTML est NETTOYÉ par l'API (défense XSS) ; le front ne sanitise pas.
 */
export interface EditCguDraftInput {
  /** Corps HTML éditorial (optionnel). NETTOYÉ côté serveur avant persistance. */
  readonly contentHtml?: string;
  /** Date d'entrée en vigueur cible (ISO), optionnelle. */
  readonly effectiveDate?: string;
}

/**
 * Erreur applicative renvoyée par l'API legal admin (HTTP 4xx). Porte le `status` HTTP pour
 * que l'UI mappe la règle métier PAR CODE (400 = contenu aberrant → message générique NON serveur ;
 * 404 = version absente ; 409 = version non-Brouillon immuable). Elle ne réinterprète jamais la
 * règle métier (autorité = API), mais NE rend jamais un message serveur brut pour un 400.
 */
export class LegalAdminApiError extends HttpApiError {
  public constructor(
    message: string,
    /** Code HTTP de la réponse d'erreur (400/404/409…), `undefined` si indisponible. */
    status?: number,
  ) {
    super(message, status, 'LegalAdminApiError');
  }
}

/**
 * Traduit une erreur d'écriture CGU en message utilisateur. Un **400** (contenu HTML aberrant,
 * rejeté par le sanitiseur serveur) NE rend JAMAIS le message serveur brut : message générique
 * fixe. 404/409 relaient le message serveur (déjà en français, propre, non sensible). Toute autre
 * erreur retombe sur `fallback`.
 */
export function cguWriteErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof LegalAdminApiError) {
    if (error.status === 400) {
      return 'Le contenu comporte du balisage non autorisé ou est invalide. Vérifiez le HTML saisi.';
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

/**
 * Contrat du client CGU ADMIN. LECTURES cross-fournisseur (S5bis) + REGISTRE des versions
 * (A-CGU ÉTAPE 2) + WRITES admin (create draft / publish / delete draft). Les writes vivent ICI
 * (et non dans le LegalClient FOURNISSEUR) car ils sont ADMIN-guarded (cookie admin + `admin_csrf`),
 * là où le client fournisseur porte `supplier_csrf` : session/CSRF distincts (décision d'archi).
 */
export interface LegalAdminClient {
  /** GET /admin/legal/cgu/signed-status — map siret → statut (colonne A-03/A-04). */
  signedStatusBySiret(): Promise<AdminSignedStatusBySiretDto>;
  /** GET /admin/legal/cgu/signatures — journal §2.9 (registre des signatures). */
  signaturesJournal(): Promise<AdminSignaturesJournalDto>;
  /** GET /admin/legal/cgu/signed-sirets — set des signataires de la version courante. */
  signedSiretsForCurrentVersion(): Promise<AdminSignedSiretsDto>;
  /**
   * GET /admin/legal/cgu/signed-contacts — signataires de la version courante au grain CONTACT
   * (FIX-FOU-2 / DEC-48). L'anti-join ALL (SIRET listé non-signataire dès qu'≥1 contact ACTIF n'a
   * pas signé) est composé DANS LE FRONT, en croisant `bySiret` avec les contacts Actifs (supplier-
   * reference). Le BC legal ne connaît pas l'état Actif des contacts (invariant n°2).
   */
  signedContactsForCurrentVersion(): Promise<AdminSignedContactsDto>;

  // --- REGISTRE des versions (A-CGU ÉTAPE 2) ---
  /** GET /admin/legal/cgu/versions — registre des versions (7 colonnes + nbSignatures). */
  listVersions(): Promise<readonly CguRegistreVersionRowDto[]>;
  /** GET /admin/legal/cgu/versions/count — nombre total de versions au registre. */
  countVersions(): Promise<{ readonly count: number }>;
  /** GET /admin/legal/cgu/versions/:id — détail (contenu HTML + métadonnées). */
  getVersion(cguVersionId: string): Promise<CguRegistreVersionDetailDto>;
  /**
   * GET /admin/legal/cgu/versions/:id/pdf — OCTETS du PDF horodaté (Publiée/Archivée uniquement).
   * 404 (Brouillon / version sans PDF) → LegalAdminApiError.
   */
  getVersionPdf(cguVersionId: string): Promise<ArrayBuffer>;

  // --- WRITES admin ---
  /**
   * POST /legal/cgu/versions — crée un BROUILLON (admin), corps `contentHtml` INCLUS et persisté
   * (le serveur nettoie le HTML). 400 (contenu aberrant) → LegalAdminApiError(status=400).
   */
  saveDraft(input: CreateCguDraftInput): Promise<{ readonly cguVersionId: string }>;
  /**
   * PATCH /admin/legal/cgu/versions/:id — édite le contenu d'un BROUILLON (patch partiel).
   * 404 (absent) / 409 (non-Brouillon immuable) / 400 (HTML aberrant) → LegalAdminApiError(status).
   */
  editDraft(
    cguVersionId: string,
    input: EditCguDraftInput,
  ): Promise<{ readonly cguVersionId: string; readonly state: string }>;
  /** POST /legal/cgu/versions/:id/publish — publie un BROUILLON (archive l'ancienne, PDF write-once). */
  publishVersion(cguVersionId: string): Promise<{ readonly state: string }>;
  /** DELETE /admin/legal/cgu/versions/:id — supprime un BROUILLON (Brouillon only ; 409 sinon). */
  deleteDraft(cguVersionId: string): Promise<void>;

  // --- ACTION par fournisseur (T5 — REF 1.7 §5) ---
  /**
   * POST /admin/legal/cgu/relance/:siret — relance la signature CGU d'un fournisseur NON-signataire
   * de la version courante. Best-effort : l'API publie l'intention (Domain Event) et le worker résout
   * l'email + envoie (Postmark). Réponse vide (202 Accepted). 409 si le SIRET a déjà signé la version
   * en vigueur (garde serveur). L'e-mail n'est JAMAIS résolu côté API (isolation BC, invariant n°2).
   */
  relanceCgu(siret: string): Promise<void>;

  /**
   * POST /admin/legal/cgu/resignature/:siret — FORCE la resignature CGU ciblée d'un fournisseur
   * (R15-GOV-04, DBU §2.9 l.2136-2144). Saisie d'un MOTIF OBLIGATOIRE (`motif`, texte libre). Effet
   * immédiat serveur : les contacts Actifs du SIRET passent en ResignatureRequise (supplier-reference)
   * + journalisation horodatée (Domain Event, motif redacté en audit). Réponse vide (202 Accepted).
   * 400 (BadRequest) si le SIRET est malformé OU si le motif est absent/vide/espaces.
   * PII : le `motif` transite UNIQUEMENT dans le corps POST (jamais dans l'URL, jamais loggé).
   */
  forceResignature(siret: string, motif: string): Promise<void>;

  /**
   * GET /admin/legal/cgu/resignature/:siret — JOURNAL des re-signatures forcées d'un fournisseur
   * (R15-GOV-04, DBU §2.9 l.2136-2144). Restitue les entrées (motif EN CLAIR, admin-relisible),
   * la plus récente en tête. ADMIN-ONLY (cookie admin + CSRF, calque des autres GET admin legal).
   * 400 si le SIRET est malformé (garde serveur).
   */
  listForcedResignatures(siret: string): Promise<readonly ForcedResignatureDto[]>;
}
